import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { posts, categories } from '@/content/blog';

type Filter = 'All' | string;

export function BlogIndexPage() {
  const [filter, setFilter] = useState<Filter>('All');
  const shown = filter === 'All' ? posts : posts.filter((p) => p.category === filter);

  return (
    <div>
      <SEO
        title="Blog — Commerce Career & Study Guides for Class 11 & 12 | ACES"
        description="Honest, practical guides for Indian commerce students and parents: choosing commerce, courses after 12th, exam strategy, study tips, and studying with AI. Free, no login."
        canonical="https://acescommerce.com/blog/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'ACES Blog',
          description: 'Career and study guidance for Indian Plus One & Plus Two commerce students.',
          url: 'https://acescommerce.com/blog/',
        }}
      />

      {/* Hero */}
      <section className="aces-hero">
        <div className="max-w-5xl mx-auto px-4 pt-16 sm:pt-20 pb-12">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            The ACES blog
          </span>
          <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-ink-900 tracking-tight leading-[1.06] max-w-2xl text-balance">
            Straight talk for <em className="italic text-accent-600">commerce students</em>
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl leading-relaxed">
            Honest, practical guides on choosing commerce, courses after 12th, cracking your
            exams, and studying smarter — written for Indian Class 11 &amp; 12 students and parents.
          </p>
        </div>
      </section>

      {/* Filter + grid */}
      <section className="bg-ink-50 border-t border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex flex-wrap gap-2 mb-8">
            {(['All', ...categories] as Filter[]).map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  aria-pressed={active}
                  className={[
                    'aces-chip',
                    active
                      ? 'aces-chip-active'
                      : '',
                  ].join(' ')}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {shown.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}/`}
                className="group aces-card-hover flex flex-col rounded-card border border-ink-200/80 bg-cream p-5 sm:p-6 shadow-card hover:border-accent-500/40"
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-700 bg-accent-50 border border-accent-200/70 px-2 py-0.5 rounded-full">{p.category}</span>
                  <span className="text-xs text-ink-500">{p.readMins} min read</span>
                </div>
                <h2 className="font-serif text-[1.2rem] text-ink-900 leading-snug tracking-tight text-balance group-hover:text-accent-700 transition-colors duration-150">{p.title}</h2>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed flex-1">{p.excerpt}</p>
                <span className="mt-3 text-sm font-medium text-accent-600 inline-flex items-center gap-1">
                  Read <span aria-hidden="true" className="transition-transform duration-150 group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
